import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

import { cx } from '@/lib/cx';

import styles from './RowMenu.module.css';

export type RowMenuItem = {
  label: string;
  icon?: ReactNode;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
  /** Draw a divider above this item. */
  separated?: boolean;
};

/**
 * The per-row action menu.
 *
 * A real menu: arrow keys move through it, Escape closes it and returns focus to the
 * trigger, and clicking outside dismisses it. Built here rather than reached for from
 * a library because it is small, and because every menu in this application should
 * behave identically.
 */
export function RowMenu({
  trigger,
  label,
  items,
}: {
  trigger: ReactNode;
  label: string;
  items: RowMenuItem[];
}) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  const enabled = items.filter((item) => !item.disabled);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
        return;
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveIndex((current) => (current + 1) % enabled.length);
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveIndex((current) => (current <= 0 ? enabled.length - 1 : current - 1));
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, enabled.length]);

  if (items.length === 0) return null;

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => {
          setOpen((current) => !current);
          setActiveIndex(-1);
        }}
      >
        {trigger}
      </button>

      {open ? (
        <div className={styles.menu} id={menuId} role="menu" aria-label={label}>
          {items.map((item, index) => (
            <div key={item.label}>
              {item.separated ? <div className={styles.separator} /> : null}
              <button
                type="button"
                role="menuitem"
                disabled={item.disabled}
                className={cx(
                  styles.item,
                  item.danger && styles.danger,
                  enabled.indexOf(item) === activeIndex && styles.active,
                )}
                onClick={() => {
                  setOpen(false);
                  item.onSelect();
                }}
                ref={(node) => {
                  if (enabled.indexOf(item) === activeIndex) node?.focus();
                }}
                tabIndex={index === 0 ? 0 : -1}
              >
                {item.icon}
                {item.label}
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
